import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Search, Link, BarChart2, ChevronDown, ChevronUp, X, RefreshCw } from 'lucide-react';
import { toast } from 'react-toastify';

// const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://check.policypulsehq.com/api';

const PLATFORM_COLORS = {
  shopify: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300', dot: 'bg-green-500' },
  ebay:    { bg: 'bg-blue-100',  text: 'text-blue-700',  border: 'border-blue-300',  dot: 'bg-blue-500'  },
  amazon:  { bg: 'bg-orange-100',text: 'text-orange-700',border: 'border-orange-300',dot: 'bg-orange-500'},
};

const fmt = (v) => `£${(parseFloat(v) || 0).toFixed(2)}`;
const fmtN = (v) => parseInt(v) || 0;

// ── Debounce hook ──────────────────────────────────────────────
function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ── Platform SKU search dropdown ───────────────────────────────
function PlatformSkuSearch({ platform, onSelect }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const debouncedQ = useDebounce(query, 300);

  useEffect(() => {
    if (!debouncedQ) { setResults([]); return; }
    const token = localStorage.getItem('token');
    fetch(`${API_BASE_URL}/sku-mapping/platform-skus/${platform}?q=${encodeURIComponent(debouncedQ)}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(d => { if (d.success) setResults(d.skus); });
  }, [debouncedQ, platform]);

  const handleSelect = (sku) => {
    onSelect(sku.platform_sku);
    setQuery(sku.platform_sku);
    setOpen(false);
    setResults([]);
  };

  return (
    <div className="relative">
      <input
        type="text"
        value={query}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={`Search ${platform} SKUs...`}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent"
      />
      {open && results.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-48 overflow-y-auto">
          {results.map((r, i) => (
            <button
              key={i}
              onClick={() => handleSelect(r)}
              className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-0"
            >
              <span className="font-mono text-sm text-gray-800">{r.platform_sku}</span>
              {r.product_name && (
                <span className="block text-xs text-gray-500 truncate">{r.product_name}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Add Mapping Modal ──────────────────────────────────────────
function AddMappingModal({ product, onClose, onSaved }) {
  const [platform, setPlatform] = useState('shopify');
  const [sku, setSku] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!sku.trim()) { toast.error('Please enter or select a SKU'); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/sku-mapping/master-products/${product.id}/mappings`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ platform, platform_sku: sku.trim() })
      });
      const data = await res.json();
      if (data.success) { toast.success('Mapping added!'); onSaved(); onClose(); }
      else toast.error(data.error);
    } catch { toast.error('Failed to add mapping'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-gray-800">Add SKU Mapping</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Mapping for: <strong className="text-gray-800">{product.product_name}</strong>
        </p>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Platform</label>
          <div className="grid grid-cols-3 gap-2">
            {['shopify', 'ebay', 'amazon'].map(p => (
              <button
                key={p}
                onClick={() => { setPlatform(p); setSku(''); }}
                className={`py-2 px-3 rounded-lg text-sm font-medium capitalize border transition-all ${
                  platform === p
                    ? `${PLATFORM_COLORS[p].bg} ${PLATFORM_COLORS[p].text} ${PLATFORM_COLORS[p].border} border`
                    : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                }`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {platform.charAt(0).toUpperCase() + platform.slice(1)} SKU
          </label>
          <PlatformSkuSearch platform={platform} onSelect={setSku} />
          <p className="text-xs text-gray-400 mt-1">Search from existing orders or type manually</p>
          {sku && (
            <div className={`mt-2 px-3 py-2 rounded-lg text-sm font-mono ${PLATFORM_COLORS[platform].bg} ${PLATFORM_COLORS[platform].text}`}>
              Selected: {sku}
            </div>
          )}
        </div>

        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 py-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 font-medium"
          >
            {saving ? 'Saving...' : 'Add Mapping'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Create Master Product Modal ────────────────────────────────
function CreateProductModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ master_sku: '', product_name: '', cost_price: '' });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.master_sku || !form.product_name) {
      toast.error('Master SKU and product name are required');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/sku-mapping/master-products`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, cost_price: parseFloat(form.cost_price) || 0 })
      });
      const data = await res.json();
      if (data.success) { toast.success('Master product created!'); onSaved(); onClose(); }
      else toast.error(data.error);
    } catch { toast.error('Failed to create product'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-bold text-gray-800">Create Master Product</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Master SKU *</label>
            <input
              type="text"
              value={form.master_sku}
              onChange={e => setForm(f => ({ ...f, master_sku: e.target.value }))}
              placeholder="e.g. LAB-COAT-S"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Product Name *</label>
            <input
              type="text"
              value={form.product_name}
              onChange={e => setForm(f => ({ ...f, product_name: e.target.value }))}
              placeholder="e.g. Lab Coat Small"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cost Price (£)</label>
            <input
              type="number"
              step="0.01"
              value={form.cost_price}
              onChange={e => setForm(f => ({ ...f, cost_price: e.target.value }))}
              placeholder="0.00"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
            />
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 py-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 font-medium"
          >
            {saving ? 'Creating...' : 'Create Product'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────
export function SkuMapping() {
  const [tab, setTab] = useState('mapping'); // 'mapping' | 'report'
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({});
  const [showCreate, setShowCreate] = useState(false);
  const [addMappingFor, setAddMappingFor] = useState(null);

  // Report state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/sku-mapping/master-products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setProducts(data.products);
    } catch { toast.error('Failed to load products'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Delete this master product and all its mappings?')) return;
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_BASE_URL}/sku-mapping/master-products/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Deleted');
      fetchProducts();
    } catch { toast.error('Failed to delete'); }
  };

  const handleDeleteMapping = async (mappingId) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_BASE_URL}/sku-mapping/mappings/${mappingId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Mapping removed');
      fetchProducts();
    } catch { toast.error('Failed to remove mapping'); }
  };

  const generateReport = async () => {
    if (!startDate || !endDate) { toast.error('Please select a date range'); return; }
    setReportLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${API_BASE_URL}/sku-mapping/unified-report?start_date=${startDate}&end_date=${endDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (data.success) {
        setReportData(data);
        toast.success(`Report generated — ${data.data.length} products found`);
      } else {
        toast.error(data.error);
      }
    } catch { toast.error('Failed to generate report'); }
    finally { setReportLoading(false); }
  };

  const filteredProducts = products.filter(p =>
    p.product_name.toLowerCase().includes(search.toLowerCase()) ||
    p.master_sku.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">SKU Mapping</h1>
          <p className="text-gray-500 mt-1">Map SKUs across Shopify, eBay & Amazon for unified reporting</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setTab('mapping')}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium transition-all ${
            tab === 'mapping'
              ? 'bg-orange-600 text-white shadow-lg'
              : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
          }`}
        >
          <Link size={18} /> SKU Mappings
          <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${tab === 'mapping' ? 'bg-orange-500' : 'bg-gray-200 text-gray-600'}`}>
            {products.length}
          </span>
        </button>
        <button
          onClick={() => setTab('report')}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium transition-all ${
            tab === 'report'
              ? 'bg-orange-600 text-white shadow-lg'
              : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
          }`}
        >
          <BarChart2 size={18} /> Unified Report
        </button>
      </div>

      {/* ── MAPPING TAB ── */}
      {tab === 'mapping' && (
        <>
          <div className="flex gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-3 text-gray-400" size={18} />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search master products..."
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <button
              onClick={() => setShowCreate(true)}
              className="flex items-center gap-2 px-5 py-2.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium shadow-lg"
            >
              <Plus size={18} /> New Master Product
            </button>
          </div>

          {loading ? (
            <div className="text-center py-16 text-gray-400">
              <RefreshCw className="animate-spin mx-auto mb-3" size={28} />
              Loading...
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="bg-white rounded-xl shadow p-12 text-center">
              <Link className="mx-auto mb-4 text-gray-300" size={48} />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No master products yet</h3>
              <p className="text-gray-400 mb-6">Create your first master product to start mapping SKUs across platforms.</p>
              <button
                onClick={() => setShowCreate(true)}
                className="px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium"
              >
                <Plus className="inline mr-2" size={18} />Create First Product
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredProducts.map(product => {
                const isExpanded = expanded[product.id];
                const mappingsByPlatform = {
                  shopify: (product.mappings || []).filter(m => m.platform === 'shopify'),
                  ebay:    (product.mappings || []).filter(m => m.platform === 'ebay'),
                  amazon:  (product.mappings || []).filter(m => m.platform === 'amazon'),
                };
                const totalMappings = (product.mappings || []).length;

                return (
                  <div key={product.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    {/* Header row */}
                    <div
                      className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => setExpanded(e => ({ ...e, [product.id]: !e[product.id] }))}
                    >
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm font-bold text-orange-600 bg-orange-50 px-2 py-0.5 rounded">
                              {product.master_sku}
                            </span>
                            <span className="font-medium text-gray-800 truncate">{product.product_name}</span>
                          </div>
                          <div className="flex items-center gap-3 mt-1">
                            <span className="text-xs text-gray-500">Cost: £{parseFloat(product.cost_price || 0).toFixed(2)}</span>
                            <span className="text-xs text-gray-400">•</span>
                            <span className="text-xs text-gray-500">{totalMappings} mapping{totalMappings !== 1 ? 's' : ''}</span>
                            {/* Platform dots */}
                            <div className="flex gap-1">
                              {['shopify','ebay','amazon'].map(p => (
                                <span
                                  key={p}
                                  className={`w-2 h-2 rounded-full ${mappingsByPlatform[p].length > 0 ? PLATFORM_COLORS[p].dot : 'bg-gray-200'}`}
                                  title={`${p}: ${mappingsByPlatform[p].length} SKU(s)`}
                                />
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <button
                          onClick={e => { e.stopPropagation(); setAddMappingFor(product); }}
                          className="flex items-center gap-1 px-3 py-1.5 text-sm bg-orange-50 text-orange-600 rounded-lg hover:bg-orange-100 font-medium"
                        >
                          <Plus size={14} /> Add SKU
                        </button>
                        <button
                          onClick={e => { e.stopPropagation(); handleDeleteProduct(product.id); }}
                          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 size={16} />
                        </button>
                        {isExpanded ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
                      </div>
                    </div>

                    {/* Expanded mappings */}
                    {isExpanded && (
                      <div className="border-t border-gray-100 p-4 bg-gray-50">
                        {totalMappings === 0 ? (
                          <p className="text-sm text-gray-400 text-center py-4">
                            No SKUs mapped yet. Click "Add SKU" to link platform SKUs.
                          </p>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {['shopify','ebay','amazon'].map(platform => (
                              <div key={platform}>
                                <div className={`flex items-center gap-2 mb-2 px-3 py-1.5 rounded-lg ${PLATFORM_COLORS[platform].bg}`}>
                                  <span className={`w-2 h-2 rounded-full ${PLATFORM_COLORS[platform].dot}`} />
                                  <span className={`text-sm font-semibold capitalize ${PLATFORM_COLORS[platform].text}`}>
                                    {platform}
                                  </span>
                                  <span className={`text-xs ${PLATFORM_COLORS[platform].text} opacity-70`}>
                                    ({mappingsByPlatform[platform].length})
                                  </span>
                                </div>
                                <div className="space-y-1">
                                  {mappingsByPlatform[platform].length === 0 ? (
                                    <p className="text-xs text-gray-400 px-2">No mapping</p>
                                  ) : (
                                    mappingsByPlatform[platform].map(m => (
                                      <div key={m.id} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-gray-200">
                                        <span className="font-mono text-sm text-gray-800">{m.platform_sku}</span>
                                        <button
                                          onClick={() => handleDeleteMapping(m.id)}
                                          className="text-gray-300 hover:text-red-500 ml-2"
                                        >
                                          <X size={14} />
                                        </button>
                                      </div>
                                    ))
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ── REPORT TAB ── */}
      {tab === 'report' && (
        <>
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Unified SKU Report</h2>
            <div className="flex flex-wrap gap-4 items-end">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <button
                onClick={generateReport}
                disabled={reportLoading}
                className="flex items-center gap-2 px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 font-medium"
              >
                <BarChart2 size={18} />
                {reportLoading ? 'Generating...' : 'Generate Report'}
              </button>
            </div>
          </div>

          {reportData && (
            <>
              {/* Summary cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {[
                  { label: 'Total Orders', value: fmtN(reportData.summary.total_orders), color: 'text-orange-600' },
                  { label: 'Total Units',  value: fmtN(reportData.summary.total_units),  color: 'text-blue-600'   },
                  { label: 'Total Revenue',value: fmt(reportData.summary.total_revenue), color: 'text-green-600'  },
                  { label: 'Total Profit', value: fmt(reportData.summary.total_profit),  color: 'text-purple-600' },
                ].map(card => (
                  <div key={card.label} className="bg-white rounded-xl shadow-sm p-5">
                    <p className="text-gray-500 text-sm mb-1">{card.label}</p>
                    <p className={`text-2xl font-bold ${card.color}`}>{card.value}</p>
                  </div>
                ))}
              </div>

              {/* Report table */}
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b-2 border-gray-200">
                      <tr>
                        <th className="text-left py-3 px-4 text-gray-600 font-semibold">Product</th>
                        {/* Shopify */}
                        <th className="text-right py-3 px-3 font-semibold">
                          <span className="flex items-center justify-end gap-1">
                            <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
                            <span className="text-green-700">Shopify</span>
                          </span>
                        </th>
                        <th className="text-right py-3 px-3 text-green-700 font-semibold">Revenue</th>
                        {/* eBay */}
                        <th className="text-right py-3 px-3 font-semibold">
                          <span className="flex items-center justify-end gap-1">
                            <span className="w-2 h-2 rounded-full bg-blue-500 inline-block" />
                            <span className="text-blue-700">eBay</span>
                          </span>
                        </th>
                        <th className="text-right py-3 px-3 text-blue-700 font-semibold">Revenue</th>
                        {/* Amazon */}
                        <th className="text-right py-3 px-3 font-semibold">
                          <span className="flex items-center justify-end gap-1">
                            <span className="w-2 h-2 rounded-full bg-orange-500 inline-block" />
                            <span className="text-orange-700">Amazon</span>
                          </span>
                        </th>
                        <th className="text-right py-3 px-3 text-orange-700 font-semibold">Revenue</th>
                        {/* Totals */}
                        <th className="text-right py-3 px-3 text-gray-600 font-semibold border-l border-gray-200">Total Units</th>
                        <th className="text-right py-3 px-3 text-gray-600 font-semibold">Total Revenue</th>
                        <th className="text-right py-3 px-3 text-gray-600 font-semibold">Profit</th>
                        <th className="text-right py-3 px-4 text-gray-600 font-semibold">Margin</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.data.map((row, i) => (
                        <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <p className="font-medium text-gray-800">{row.product_name}</p>
                            <p className="text-xs font-mono text-gray-400">{row.master_sku}</p>
                          </td>
                          {/* Shopify */}
                          <td className="py-3 px-3 text-right text-green-700">{fmtN(row.shopify.units)}</td>
                          <td className="py-3 px-3 text-right text-green-700">{fmt(row.shopify.revenue)}</td>
                          {/* eBay */}
                          <td className="py-3 px-3 text-right text-blue-700">{fmtN(row.ebay.units)}</td>
                          <td className="py-3 px-3 text-right text-blue-700">{fmt(row.ebay.revenue)}</td>
                          {/* Amazon */}
                          <td className="py-3 px-3 text-right text-orange-700">{fmtN(row.amazon.units)}</td>
                          <td className="py-3 px-3 text-right text-orange-700">{fmt(row.amazon.revenue)}</td>
                          {/* Totals */}
                          <td className="py-3 px-3 text-right font-semibold text-gray-800 border-l border-gray-100">{fmtN(row.total_units)}</td>
                          <td className="py-3 px-3 text-right font-semibold text-gray-800">{fmt(row.total_revenue)}</td>
                          <td className="py-3 px-3 text-right font-bold text-green-600">{fmt(row.total_profit)}</td>
                          <td className="py-3 px-4 text-right">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${
                              parseFloat(row.profit_margin) > 20 ? 'bg-green-100 text-green-700' :
                              parseFloat(row.profit_margin) > 0  ? 'bg-yellow-100 text-yellow-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {row.profit_margin}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {reportData.data.length === 0 && (
                  <div className="text-center py-12 text-gray-400">
                    No sales found for mapped products in this date range.
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}

      {/* Modals */}
      {showCreate && (
        <CreateProductModal onClose={() => setShowCreate(false)} onSaved={fetchProducts} />
      )}
      {addMappingFor && (
        <AddMappingModal
          product={addMappingFor}
          onClose={() => setAddMappingFor(null)}
          onSaved={fetchProducts}
        />
      )}
    </div>
  );
}

export default SkuMapping;